import { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { 
  BookMarked, Download, Printer, Mail, RefreshCw, 
  User, Palette, Check, Copy, Globe, BookOpen, Upload, Image, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = window.location.origin;

// Plantillas de diseño para separadores
const BOOKMARK_TEMPLATES = [
  {
    id: 'classic',
    name: 'Clásico',
    bgColor: 'bg-white',
    textColor: 'text-gray-900',
    accentColor: 'bg-violet-600',
    borderColor: 'border-gray-200'
  },
  {
    id: 'dark',
    name: 'Elegante Oscuro',
    bgColor: 'bg-gray-900',
    textColor: 'text-white',
    accentColor: 'bg-violet-500',
    borderColor: 'border-gray-700'
  },
  {
    id: 'gradient',
    name: 'Gradiente Viubux',
    bgColor: 'bg-gradient-to-b from-violet-600 via-purple-600 to-violet-800',
    textColor: 'text-white',
    accentColor: 'bg-white',
    borderColor: 'border-transparent'
  },
  {
    id: 'cream',
    name: 'Crema Literario',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-900',
    accentColor: 'bg-amber-700',
    borderColor: 'border-amber-200'
  }
];

const BookmarkGenerator = ({ user, token }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('gradient');
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedWork, setSelectedWork] = useState(null);
  const [customImage, setCustomImage] = useState(null);
  const [customImagePreview, setCustomImagePreview] = useState(null);
  const bookmarkRef = useRef(null);
  const fileInputRef = useRef(null);

  const profileUrl = `${SITE_URL}/${user?.username}`;

  useEffect(() => {
    fetchProfileData();
  }, [token]);

  const fetchProfileData = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileData(response.data);
      // Seleccionar la primera obra si existe
      if (response.data.works && response.data.works.length > 0) {
        setSelectedWork(response.data.works[0]);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentTemplate = BOOKMARK_TEMPLATES.find(t => t.id === selectedTemplate) || BOOKMARK_TEMPLATES[0];

  // Manejar subida de imagen personalizada
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La imagen debe ser menor a 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setCustomImage(file);
        setCustomImagePreview(event.target.result);
        toast.success('Imagen cargada correctamente');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCustomImage = () => {
    setCustomImage(null);
    setCustomImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadBookmark = async () => {
    if (!bookmarkRef.current) return;

    setDownloading(true);
    try {
      const canvas = await html2canvas(bookmarkRef.current, {
        scale: 4, // Mayor resolución para evitar pixelado
        backgroundColor: null,
        useCORS: true,
        logging: false,
        allowTaint: true,
        width: bookmarkRef.current.offsetWidth,
        height: bookmarkRef.current.offsetHeight
      });

      const link = document.createElement('a');
      link.download = `separador-${user?.username}-${selectedTemplate}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      toast.success('Separador descargado');
    } catch (error) {
      console.error('Error downloading bookmark:', error);
      toast.error('Error al descargar el separador');
    } finally {
      setDownloading(false);
    }
  };

  const copyProfileUrl = () => {
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    toast.success('Enlace copiado');
    setTimeout(() => setCopied(false), 2000);
  };

  const requestPrint = () => {
    const subject = encodeURIComponent(`Solicitud de Impresión de Separadores - ${user?.full_name}`);
    const body = encodeURIComponent(
      `Hola,\n\n` +
      `Me gustaría solicitar la impresión de separadores de libros.\n\n` +
      `--- DATOS DEL PEDIDO ---\n` +
      `Nombre: ${user?.full_name}\n` +
      `Email: ${user?.email}\n` +
      `Usuario: @${user?.username}\n` +
      `Plantilla seleccionada: ${currentTemplate.name}\n` +
      `Libro: ${selectedWork?.title || 'General'}\n` +
      `Enlace de perfil: ${profileUrl}\n\n` +
      `Por favor, adjunto el diseño del separador.\n\n` +
      `Gracias.`
    );
    window.location.href = `mailto:print@viubux.com?subject=${subject}&body=${body}`;
    toast.success('Abriendo cliente de correo...');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
          <BookMarked className="w-5 h-5 text-violet-600" />
          Generador de Separadores
        </h3>
        <p className="text-sm text-gray-600">
          Crea separadores de libros personalizados con tu QR, portada y datos. Perfectos para regalar a tus lectores.
        </p>
      </div>

      {/* Work Selector (if author has works) */}
      {profileData?.works && profileData.works.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-violet-600" />
            Selecciona tu Obra
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {profileData.works.map((work) => (
              <button
                key={work.isbn || work.title}
                onClick={() => setSelectedWork(work)}
                className={`relative p-3 rounded-xl border-2 transition-all text-left ${
                  selectedWork?.title === work.title
                    ? 'border-violet-500 ring-2 ring-violet-200 bg-violet-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {work.cover_image ? (
                  <img 
                    src={work.cover_image} 
                    alt={work.title}
                    className="w-full h-20 object-cover rounded-lg mb-2"
                  />
                ) : (
                  <div className="w-full h-20 bg-gray-100 rounded-lg mb-2 flex items-center justify-center">
                    <BookOpen className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <span className="text-xs font-medium text-gray-700 line-clamp-2">{work.title}</span>
                {selectedWork?.title === work.title && (
                  <div className="absolute top-2 right-2 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Template Selector */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Palette className="w-4 h-4 text-violet-600" />
          Selecciona un Diseño
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {BOOKMARK_TEMPLATES.map((template) => (
            <button
              key={template.id}
              onClick={() => setSelectedTemplate(template.id)}
              className={`relative p-4 rounded-xl border-2 transition-all ${
                selectedTemplate === template.id
                  ? 'border-violet-500 ring-2 ring-violet-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`w-8 h-20 mx-auto rounded ${template.bgColor} ${template.borderColor} border mb-2`} />
              <span className="text-sm font-medium text-gray-700">{template.name}</span>
              {selectedTemplate === template.id && (
                <div className="absolute top-2 right-2 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Image Upload */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Image className="w-4 h-4 text-violet-600" />
          Imagen Personalizada (Opcional)
        </h4>
        <p className="text-sm text-gray-500 mb-4">
          Sube tu propia imagen para usar en el separador en lugar de la portada del libro o foto de perfil.
        </p>
        
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageUpload}
          accept="image/*"
          className="hidden"
          id="custom-image-upload"
        />
        
        {customImagePreview ? (
          <div className="flex items-center gap-4">
            <img 
              src={customImagePreview} 
              alt="Imagen personalizada" 
              className="w-20 h-20 object-cover rounded-lg border border-gray-200"
            />
            <div className="flex-1">
              <p className="text-sm text-gray-700 font-medium">Imagen cargada</p>
              <p className="text-xs text-gray-500">Se usará esta imagen en el separador</p>
            </div>
            <Button
              onClick={removeCustomImage}
              variant="outline"
              size="sm"
              className="text-red-500 border-red-200 hover:bg-red-50"
            >
              <X className="w-4 h-4 mr-1" />
              Quitar
            </Button>
          </div>
        ) : (
          <label 
            htmlFor="custom-image-upload"
            className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-violet-400 hover:bg-violet-50 transition-colors"
          >
            <Upload className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-600">Haz clic para subir una imagen</span>
          </label>
        )}
      </div>

      {/* Bookmark Preview */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-medium text-gray-900 mb-4">Vista Previa del Separador</h4>
        
        <div className="flex flex-col items-center">
          {/* Bookmark */}
          <div
            ref={bookmarkRef}
            className={`w-[200px] h-[520px] rounded-xl p-4 ${currentTemplate.bgColor} ${currentTemplate.borderColor} border shadow-xl flex flex-col items-center justify-between overflow-hidden`}
            style={{ fontSmooth: 'always', WebkitFontSmoothing: 'antialiased' }}
          >
            {/* Top: Logo/Brand */}
            <div className="text-center flex-shrink-0">
              <div className={`w-12 h-12 mx-auto rounded-full ${currentTemplate.accentColor} flex items-center justify-center mb-2`}>
                <BookMarked className={`w-6 h-6 ${currentTemplate.id === 'gradient' || currentTemplate.id === 'cream' ? 'text-violet-600' : 'text-white'}`} />
              </div>
              <p className={`text-xs font-bold ${currentTemplate.textColor} opacity-80`}>VIUBUX</p>
            </div>

            {/* Middle: Custom Image, Book Cover or Author Photo */}
            <div className="flex-1 flex flex-col items-center justify-center py-4 min-h-0">
              {customImagePreview ? (
                <img
                  src={customImagePreview}
                  alt="Imagen personalizada"
                  className="w-32 h-44 object-cover rounded-lg shadow-md"
                  crossOrigin="anonymous"
                />
              ) : selectedWork?.cover_image ? (
                <img
                  src={selectedWork.cover_image}
                  alt={selectedWork.title}
                  className="w-32 h-44 object-cover rounded-lg shadow-md"
                  crossOrigin="anonymous"
                />
              ) : user?.profile_photo ? (
                <img
                  src={user.profile_photo}
                  alt={user.full_name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white/30 shadow-md"
                  crossOrigin="anonymous"
                />
              ) : (
                <div className={`w-24 h-24 rounded-full ${currentTemplate.accentColor} flex items-center justify-center`}>
                  <User className={`w-12 h-12 ${currentTemplate.id === 'gradient' || currentTemplate.id === 'cream' ? 'text-violet-600' : 'text-white'}`} />
                </div>
              )}
              
              <div className="mt-3 text-center px-2 w-full">
                <p className={`text-sm font-bold ${currentTemplate.textColor} leading-tight break-words`} style={{ wordBreak: 'break-word', hyphens: 'auto' }}>
                  {selectedWork?.title || user?.full_name}
                </p>
                {selectedWork && (
                  <p className={`text-xs ${currentTemplate.textColor} opacity-70 mt-1 break-words`}>
                    {user?.full_name}
                  </p>
                )}
              </div>
            </div>

            {/* Bottom: QR Code */}
            <div className="text-center flex-shrink-0">
              <div className="bg-white p-2 rounded-lg inline-block shadow-sm">
                <QRCodeSVG
                  value={profileUrl}
                  size={56}
                  level="H"
                  includeMargin={false}
                  imageSettings={{
                    src: "/viubux_logo_web.png",
                    height: 10,
                    width: 10,
                    excavate: true,
                  }}
                />
              </div>
              <p className={`text-[9px] ${currentTemplate.textColor} opacity-60 mt-1`}>
                Escanea y descubre más
              </p>
            </div>
          </div>

          {/* Download Button */}
          <div className="mt-6">
            <Button
              onClick={downloadBookmark}
              disabled={downloading}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {downloading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Descargar Separador
            </Button>
          </div>
        </div>
      </div>

      {/* Profile URL */}
      <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <Globe className="w-5 h-5 text-violet-600 flex-shrink-0" />
            <code className="text-sm text-violet-700 truncate">{profileUrl}</code>
          </div>
          <Button
            onClick={copyProfileUrl}
            variant="outline"
            size="sm"
            className="text-violet-600 border-violet-300 flex-shrink-0"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Print Service CTA */}
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center shrink-0">
            <Printer className="w-8 h-8" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl font-bold mb-1">¿Quieres separadores impresos?</h3>
            <p className="text-white/90 text-sm">
              Impresión profesional en cartulina premium. Perfectos para regalar en firmas de libros o eventos.
            </p>
            <p className="text-2xl font-bold mt-2">500 separadores desde $497 MXN</p>
          </div>
          <Button
            onClick={requestPrint}
            className="bg-white text-emerald-600 hover:bg-emerald-50 shrink-0 font-semibold"
          >
            <Mail className="w-4 h-4 mr-2" />
            Solicitar Impresión
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookmarkGenerator;
